// Client Work Planner — management command center. KPIs, task analytics with a
// period selector, charts (completed-by-client, status donut, weekly trend),
// top-performers and attention widgets, and a sortable/filterable client
// performance ranking (List default, Card toggle). All ranking/charts honour the
// selected period. Preserves add/delete client, inline status, and search.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { startOfMonth, endOfMonth, subMonths } from "date-fns";
import {
  Search, Building2, ArrowRight, ListChecks, X, Plus, Trash2, Loader2,
  LayoutGrid, List as ListIcon, TrendingUp, AlertTriangle, Trophy, Users,
  Tag as TagIcon, Pencil, Check,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, LabelList,
  PieChart, Pie, AreaChart, Area, CartesianGrid,
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { workPlannerApi } from "@/services/workPlannerApi";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const C = { completed: "#16a34a", in_progress: "#2563eb", pending: "#f59e0b", overdue: "#dc2626", indigo: "#6366f1" };

const CLIENT_STATUS = {
  active: { label: "Active", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200", dot: "bg-emerald-500" },
  inactive: { label: "Inactive", chip: "bg-slate-100 text-slate-600 ring-slate-200", dot: "bg-slate-400" },
  foc: { label: "FOC", chip: "bg-amber-50 text-amber-700 ring-amber-200", dot: "bg-amber-500" },
};
const clientStatusOf = (c) => {
  const s = String((c && c.client_status) || "active").toLowerCase();
  return CLIENT_STATUS[s] ? s : "active";
};
const initials = (name) => {
  const n = (name || "").trim();
  if (!n) return "?";
  return n.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
};
const nameOf = (c) => c.client_name || c.name || "Unnamed client";
const locOf = (c) => c.city || c.company_name || c.service_taken || "";

function StatusBadge({ status }) {
  const m = CLIENT_STATUS[status] || CLIENT_STATUS.active;
  return <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${m.chip}`}>{m.label}</span>;
}
function StatusSelect({ status, onChange }) {
  const m = CLIENT_STATUS[status] || CLIENT_STATUS.active;
  return (
    <select value={status} onClick={(e) => e.stopPropagation()} onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
      className={`shrink-0 cursor-pointer rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset outline-none ${m.chip}`}>
      {Object.entries(CLIENT_STATUS).map(([v, meta]) => <option key={v} value={v}>{meta.label}</option>)}
    </select>
  );
}
function ProgressBar({ pct, color = C.completed }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-full max-w-[120px] overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="w-9 shrink-0 text-right text-xs font-semibold text-slate-700">{pct}%</span>
    </div>
  );
}

const matchTag = (tg, q) => !q || (tg.name || "").toLowerCase().includes(q.toLowerCase().trim());

function TagPills({ ids, tagById, max = 4 }) {
  const list = (ids || []).map((id) => tagById[id]).filter(Boolean);
  if (list.length === 0) return null;
  const shown = list.slice(0, max), extra = list.length - shown.length;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {shown.map((tg) => (
        <span key={tg.tag_id} className="inline-flex items-center rounded-md px-2 py-0.5 text-[13px] font-medium leading-5"
          style={{ backgroundColor: (tg.color || "#6366f1") + "1a", color: tg.color || "#6366f1" }}>{tg.name}</span>
      ))}
      {extra > 0 && <span className="text-[13px] font-medium text-slate-400">+{extra}</span>}
    </span>
  );
}

// Searchable, multi-select tag checklist — shared by the dashboard filter and
// the per-client tag editor so both behave identically as the catalog grows.
function TagCheckList({ tags, selected, onToggle, maxHeight = "15rem", autoFocus = false }) {
  const [q, setQ] = useState("");
  const shown = tags.filter((tg) => matchTag(tg, q));
  return (
    <>
      <div className="relative mb-1.5">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus={autoFocus} data-testid="wp-tag-search" placeholder="Search tags…"
          className="h-9 w-full rounded-lg border border-slate-200 pl-8 pr-7 text-sm text-slate-800 placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none" />
        {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100"><X className="h-3.5 w-3.5" /></button>}
      </div>
      <div className="overflow-y-auto" style={{ maxHeight }}>
        {tags.length === 0 ? (
          <div className="px-1 py-3 text-center text-sm text-slate-400">No tags yet.</div>
        ) : shown.length === 0 ? (
          <div className="px-1 py-3 text-center text-sm text-slate-400">No tags found</div>
        ) : shown.map((tg) => {
          const on = selected.includes(tg.tag_id);
          return (
            <button key={tg.tag_id} onClick={() => onToggle(tg.tag_id)} className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm font-medium transition hover:bg-slate-50 ${on ? "text-slate-900" : "text-slate-700"}`}>
              <span className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border ${on ? "border-transparent" : "border-slate-300"}`} style={on ? { backgroundColor: tg.color || "#6366f1" } : {}}>{on && <Check className="h-3.5 w-3.5 text-white" />}</span>
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: tg.color || "#6366f1" }} />
              <span className="truncate">{tg.name}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

// Popover to assign/remove a client's tags from the catalog.
function TagEditorPopover({ tags, selected, onToggle, onClose }) {
  return (
    <div onClick={(e) => e.stopPropagation()} className="absolute right-0 z-30 mt-1 w-64 rounded-xl border border-slate-200 bg-white p-2 text-left shadow-lg">
      <div className="mb-1.5 flex items-center justify-between px-1">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500"><TagIcon className="h-3.5 w-3.5 text-indigo-500" /> Service tags</span>
        <button onClick={onClose} className="rounded p-0.5 hover:bg-slate-100"><X className="h-3.5 w-3.5 text-slate-400" /></button>
      </div>
      <TagCheckList tags={tags} selected={selected} onToggle={onToggle} maxHeight="13rem" autoFocus />
    </div>
  );
}

const SORTS = [
  ["most_completed", "Most Tasks Completed"],
  ["highest_pct", "Highest Completion %"],
  ["most_total", "Most Total Tasks"],
  ["most_pending", "Most Pending"],
  ["most_overdue", "Most Overdue"],
  ["name_asc", "Client Name A–Z"],
  ["name_desc", "Client Name Z–A"],
];

export default function WorkPlannerDashboard() {
  const navigate = useNavigate();
  const { businessId } = useBusiness();
  const [summary, setSummary] = useState({ summaries: {}, totals: null, trend: [] });
  const [datePreset, setDatePreset] = useState("this_month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [tags, setTags] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [hasPending, setHasPending] = useState(false);
  const [hasOverdue, setHasOverdue] = useState(false);
  const [tagFilter, setTagFilter] = useState([]); // tag_ids
  const [tagMatch, setTagMatch] = useState("any"); // any | all
  const [tagMenuOpen, setTagMenuOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [tagEditorFor, setTagEditorFor] = useState(null); // client id whose tag popover is open
  const tagMenuRef = useRef(null);
  const [sortBy, setSortBy] = useState("most_completed");
  const [viewMode, setViewMode] = useState("list");
  const [visible, setVisible] = useState(20);

  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState({ client_name: "", company_name: "", contact_person_name: "", contact_email: "", contact_phone: "", city: "", service_taken: "", notes: "", client_status: "active", client_tags: [] });

  const fetchClients = () => {
    const url = businessId ? `${BACKEND_URL}/api/clients?business_id=${businessId}` : `${BACKEND_URL}/api/clients`;
    return fetch(url, { credentials: "include" }).then((r) => r.json())
      .then((d) => setClients(Array.isArray(d) ? d : [])).catch(() => setClients([])).finally(() => setLoadingClients(false));
  };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then(setCurrentUser).catch(() => {});
    setLoadingClients(true); fetchClients();
    // eslint-disable-next-line
  }, [businessId]);

  const dateRange = useMemo(() => {
    const now = new Date();
    if (datePreset === "this_month") return { start: startOfMonth(now), end: endOfMonth(now), label: "This month" };
    if (datePreset === "last_month") { const d = subMonths(now, 1); return { start: startOfMonth(d), end: endOfMonth(d), label: "Last month" }; }
    if (datePreset === "custom" && customStart && customEnd) return { start: new Date(customStart + "T00:00:00"), end: new Date(customEnd + "T23:59:59"), label: `${customStart} → ${customEnd}` };
    return { start: null, end: null, label: "All time" };
  }, [datePreset, customStart, customEnd]);

  const loadTags = () => workPlannerApi.listTags({ business_id: businessId || undefined }).then((d) => setTags(d.tags || [])).catch(() => setTags([]));
  useEffect(() => { loadTags(); /* eslint-disable-next-line */ }, [businessId]);

  const tagById = useMemo(() => Object.fromEntries(tags.map((t) => [t.tag_id, t])), [tags]);
  const clientTagIds = (c) => (Array.isArray(c.client_tags) ? c.client_tags : []);
  const matchesTags = (c) => {
    if (tagFilter.length === 0) return true;
    const ct = clientTagIds(c);
    return tagMatch === "all" ? tagFilter.every((id) => ct.includes(id)) : tagFilter.some((id) => ct.includes(id));
  };

  // Client ids in the current scope (status + tags) — scopes the backend trend
  // so it matches the tag/status-aware KPIs and charts.
  const scopeClientIds = useMemo(() => {
    if (tagFilter.length === 0 && statusFilter === "all") return null;
    return clients.filter((c) => (statusFilter === "all" || clientStatusOf(c) === statusFilter) && matchesTags(c)).map((c) => c.client_id || c._id);
    // eslint-disable-next-line
  }, [clients, tagFilter, tagMatch, statusFilter]);
  const scopeKey = scopeClientIds ? scopeClientIds.join(",") : "";

  useEffect(() => {
    const params = { business_id: businessId || undefined };
    if (dateRange.start) params.start = dateRange.start.toISOString();
    if (dateRange.end) params.end = dateRange.end.toISOString();
    if (scopeKey) params.client_ids = scopeKey;
    workPlannerApi.clientTaskSummary(params).then((d) => setSummary({ summaries: d.summaries || {}, totals: d.totals || null, trend: d.trend || [] })).catch(() => setSummary({ summaries: {}, totals: null, trend: [] }));
  }, [businessId, dateRange, scopeKey]);

  useEffect(() => {
    const onDoc = (e) => { if (tagMenuRef.current && !tagMenuRef.current.contains(e.target)) setTagMenuOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const saveClientTags = async (client, tagIds) => {
    const id = client.client_id || client._id;
    setClients((prev) => prev.map((x) => ((x.client_id || x._id) === id ? { ...x, client_tags: tagIds } : x)));
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/${id}`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ client_tags: tagIds }) });
      if (!res.ok) throw new Error();
    } catch { toast.error("Failed to update tags"); fetchClients(); }
  };

  const canDelete = currentUser && ["super_admin", "admin"].includes(currentUser.role);
  const canManageTags = canDelete;

  const handleCreate = async () => {
    if (!form.client_name.trim()) return toast.error("Client name is required");
    setAdding(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, client_name: form.client_name.trim(), business_id: businessId || undefined }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to add client");
      toast.success("Client added"); setAddOpen(false);
      setForm({ client_name: "", company_name: "", contact_person_name: "", contact_email: "", contact_phone: "", city: "", service_taken: "", notes: "", client_status: "active", client_tags: [] });
      await fetchClients();
      if (data.client_id || data._id) navigate(`/work-planner/client/${data.client_id || data._id}`);
    } catch (e) { toast.error(e.message); } finally { setAdding(false); }
  };

  const handleDelete = async (e, c) => {
    e.stopPropagation();
    const id = c.client_id || c._id;
    if (!window.confirm(`Delete client "${nameOf(c)}"? The client is removed; any tasks already logged against them remain in the system.`)) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/${id}`, { method: "DELETE", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to delete client");
      toast.success("Client deleted"); setClients((prev) => prev.filter((x) => (x.client_id || x._id) !== id));
    } catch (err) { toast.error(err.message); } finally { setDeletingId(null); }
  };

  const changeStatus = async (c, status) => {
    const id = c.client_id || c._id;
    setClients((prev) => prev.map((x) => ((x.client_id || x._id) === id ? { ...x, client_status: status } : x)));
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/${id}`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ client_status: status }) });
      if (!res.ok) throw new Error();
    } catch { toast.error("Failed to update status"); fetchClients(); }
  };

  const counts = useMemo(() => {
    const c = { total: clients.length, active: 0, inactive: 0, foc: 0 };
    for (const cl of clients) c[clientStatusOf(cl)]++;
    return c;
  }, [clients]);

  // Merge clients with their period stats.
  const rows = useMemo(() => clients.map((c) => {
    const id = c.client_id || c._id;
    const st = summary.summaries[id] || { total: 0, completed: 0, in_progress: 0, pending: 0, overdue: 0, cancelled: 0 };
    const base = st.total; // Completion % = Completed ÷ Total
    const pct = base ? Math.round((st.completed / base) * 100) : 0;
    return { id, client: c, name: nameOf(c), loc: locOf(c), status: clientStatusOf(c), ...st, pct };
  }), [clients, summary]);

  // Dashboard scope: status + tags drive KPIs, charts and the ranking base.
  const scopedRows = useMemo(() => rows.filter((r) => (statusFilter === "all" || r.status === statusFilter) && matchesTags(r.client)),
    // eslint-disable-next-line
    [rows, statusFilter, tagFilter, tagMatch]);

  // Table refinements: search + pending/overdue toggles + sort (do not affect KPIs/charts).
  const sortedRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = scopedRows.filter((r) => {
      if (hasPending && r.pending === 0) return false;
      if (hasOverdue && r.overdue === 0) return false;
      if (q && !(r.name.toLowerCase().includes(q) || (r.client.company_name || "").toLowerCase().includes(q))) return false;
      return true;
    });
    const cmp = {
      most_completed: (a, b) => b.completed - a.completed || b.total - a.total,
      highest_pct: (a, b) => b.pct - a.pct || b.completed - a.completed,
      most_total: (a, b) => b.total - a.total,
      most_pending: (a, b) => b.pending - a.pending,
      most_overdue: (a, b) => b.overdue - a.overdue,
      name_asc: (a, b) => a.name.localeCompare(b.name),
      name_desc: (a, b) => b.name.localeCompare(a.name),
    }[sortBy];
    return arr.sort(cmp);
  }, [scopedRows, search, hasPending, hasOverdue, sortBy]);

  useEffect(() => { setVisible(20); }, [search, statusFilter, hasPending, hasOverdue, sortBy, businessId, datePreset, scopeKey]);

  // Totals for the current scope (tag/status aware) — drive KPI cards + donut.
  const t = useMemo(() => scopedRows.reduce((a, r) => {
    a.total += r.total; a.completed += r.completed; a.in_progress += r.in_progress; a.pending += r.pending; a.overdue += r.overdue; return a;
  }, { total: 0, completed: 0, in_progress: 0, pending: 0, overdue: 0 }), [scopedRows]);

  const barData = useMemo(() => scopedRows.filter((r) => r.completed > 0).sort((a, b) => b.completed - a.completed).slice(0, 10)
    .map((r) => ({ name: r.name.length > 22 ? r.name.slice(0, 21) + "…" : r.name, completed: r.completed })), [scopedRows]);
  const donutData = useMemo(() => [
    { key: "completed", name: "Completed", value: t.completed, color: C.completed },
    { key: "in_progress", name: "In Progress", value: t.in_progress, color: C.in_progress },
    { key: "pending", name: "Pending", value: t.pending, color: C.pending },
    { key: "overdue", name: "Overdue", value: t.overdue, color: C.overdue },
  ].filter((d) => d.value > 0), [t]);

  const topPerformers = useMemo(() => scopedRows.filter((r) => r.total >= 1).sort((a, b) => b.pct - a.pct || b.completed - a.completed).slice(0, 5), [scopedRows]);
  const attention = useMemo(() => scopedRows.filter((r) => r.overdue > 0 || r.pending >= 3 || (r.total > 0 && r.pct < 40) || (r.total === 0 && r.status === "active"))
    .sort((a, b) => b.overdue - a.overdue || b.pending - a.pending || a.pct - b.pct).slice(0, 6), [scopedRows]);

  const kpiClient = [
    ["Total Clients", counts.total, "text-slate-900", <Building2 className="h-4 w-4 text-indigo-500" />],
    ["Active", counts.active, "text-emerald-600", <span className="h-2 w-2 rounded-full bg-emerald-500" />],
    ["Inactive", counts.inactive, "text-slate-600", <span className="h-2 w-2 rounded-full bg-slate-400" />],
    ["FOC", counts.foc, "text-amber-600", <span className="rounded bg-amber-100 px-1 text-[9px] font-bold text-amber-700">FOC</span>],
  ];
  const kpiTask = [
    ["Total Tasks", "total", "text-slate-900"], ["Completed", "completed", "text-green-600"],
    ["In Progress", "in_progress", "text-blue-600"], ["Pending", "pending", "text-amber-600"], ["Overdue", "overdue", "text-rose-600"],
  ];

  const openClient = (c) => navigate(`/work-planner/client/${c.client_id || c._id}`);

  const filterPills = [
    ["all", `All Clients (${counts.total})`, statusFilter === "all" && !hasPending && !hasOverdue, () => { setStatusFilter("all"); setHasPending(false); setHasOverdue(false); }],
    ["active", `Active (${counts.active})`, statusFilter === "active", () => setStatusFilter(statusFilter === "active" ? "all" : "active")],
    ["inactive", `Inactive (${counts.inactive})`, statusFilter === "inactive", () => setStatusFilter(statusFilter === "inactive" ? "all" : "inactive")],
    ["foc", `FOC (${counts.foc})`, statusFilter === "foc", () => setStatusFilter(statusFilter === "foc" ? "all" : "foc")],
    ["pending", "Has Pending", hasPending, () => setHasPending((v) => !v)],
    ["overdue", "Has Overdue", hasOverdue, () => setHasOverdue((v) => !v)],
  ];

  return (
    <div className="mx-auto max-w-[1400px] space-y-5 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Client Work Planner</h1>
          <p className="mt-1 text-sm text-slate-600">Plan, assign and track each client's monthly work.</p>
        </div>
        <div className="flex items-center gap-2 self-start">
          <Link to="/work-planner/list" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:border-slate-300"><ListChecks className="h-4 w-4" /> View all activities</Link>
          <button onClick={() => setAddOpen(true)} data-testid="wp-add-client" className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"><Plus className="h-4 w-4" /> New Client</button>
        </div>
      </div>

      {/* Client KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {kpiClient.map(([label, val, cls, icon]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-600">{icon} {label}</div>
            <div className={`mt-1 text-2xl font-semibold ${cls}`}>{val}</div>
          </div>
        ))}
      </div>

      {/* Task Performance Overview + period */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-900">Task Performance · <span className="text-slate-500">{dateRange.label}</span></h2>
          <div className="flex flex-wrap items-center gap-1.5">
            {[["this_month", "This Month"], ["last_month", "Last Month"], ["custom", "Custom"], ["all", "All Time"]].map(([v, l]) => (
              <button key={v} onClick={() => setDatePreset(v)} data-testid={`wp-date-${v}`} className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${datePreset === v ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
            ))}
            {datePreset === "custom" && (
              <span className="flex items-center gap-1">
                <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="h-7 rounded-lg border border-slate-200 px-2 text-xs" />
                <span className="text-slate-400">→</span>
                <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="h-7 rounded-lg border border-slate-200 px-2 text-xs" />
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 md:grid-cols-5">
          {kpiTask.map(([label, key, cls]) => (
            <div key={key} className="rounded-xl bg-slate-50 p-3">
              <div className={`text-2xl font-semibold ${cls}`}>{t[key] ?? 0}</div>
              <div className="text-xs text-slate-600">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Analytics */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 lg:col-span-2">
          <h3 className="mb-2 text-sm font-semibold text-slate-900">Tasks Completed by Client <span className="font-normal text-slate-500">· top {barData.length}</span></h3>
          {barData.length === 0 ? <div className="py-12 text-center text-sm text-slate-400">No completed tasks in this period.</div> : (
            <ResponsiveContainer width="100%" height={Math.max(200, barData.length * 34)}>
              <BarChart data={barData} layout="vertical" margin={{ left: 8, right: 28, top: 4, bottom: 4 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="completed" fill={C.completed} radius={[0, 6, 6, 0]} barSize={16}>
                  <LabelList dataKey="completed" position="right" style={{ fontSize: 11, fill: "#334155" }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="mb-2 text-sm font-semibold text-slate-900">Task Status</h3>
          {donutData.length === 0 ? <div className="py-12 text-center text-sm text-slate-400">No tasks in this period.</div> : (
            <div className="relative">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={donutData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={58} outerRadius={82} paddingAngle={2} stroke="none">
                    {donutData.map((d) => <Cell key={d.key} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-2xl font-semibold text-slate-900">{t.total}</div>
                <div className="text-[11px] text-slate-500">Total Tasks</div>
              </div>
            </div>
          )}
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {donutData.map((d) => (
              <div key={d.key} className="flex items-center gap-1.5 text-[11px] text-slate-600"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: d.color }} />{d.name} <span className="ml-auto font-semibold text-slate-800">{d.value}</span></div>
            ))}
          </div>
        </div>
      </div>

      {/* Trend */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-900"><TrendingUp className="h-4 w-4 text-indigo-500" /> Weekly Completion Trend <span className="font-normal text-slate-500">· planned vs completed</span></h3>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={summary.trend} margin={{ left: -18, right: 10, top: 6, bottom: 0 }}>
            <defs>
              <linearGradient id="gP" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.indigo} stopOpacity={0.25} /><stop offset="100%" stopColor={C.indigo} stopOpacity={0} /></linearGradient>
              <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.completed} stopOpacity={0.3} /><stop offset="100%" stopColor={C.completed} stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Area type="monotone" dataKey="planned" name="Planned" stroke={C.indigo} strokeWidth={2} fill="url(#gP)" />
            <Area type="monotone" dataKey="completed" name="Completed" stroke={C.completed} strokeWidth={2} fill="url(#gC)" />
          </AreaChart>
        </ResponsiveContainer>
        <div className="mt-1 flex gap-4 text-[11px] text-slate-600">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: C.indigo }} /> Planned</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: C.completed }} /> Completed</span>
        </div>
      </div>

      {/* Widgets */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Trophy className="h-4 w-4 text-amber-500" /> Top Performing Clients</h3>
          {topPerformers.length === 0 ? <div className="py-6 text-center text-sm text-slate-400">No client tasks in this period.</div> : (
            <div className="space-y-2.5">
              {topPerformers.map((r, i) => (
                <button key={r.id} onClick={() => openClient(r.client)} className="flex w-full items-center gap-3 text-left">
                  <span className="w-5 text-center text-sm">{["🥇", "🥈", "🥉"][i] || <span className="text-slate-400">{i + 1}</span>}</span>
                  <span className="w-32 shrink-0 truncate text-sm font-medium text-slate-800">{r.name}</span>
                  <ProgressBar pct={r.pct} />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-900"><AlertTriangle className="h-4 w-4 text-rose-500" /> Clients Requiring Attention</h3>
          {attention.length === 0 ? <div className="py-6 text-center text-sm text-slate-400">Nothing needs attention — nice.</div> : (
            <div className="space-y-1.5">
              {attention.map((r) => (
                <button key={r.id} onClick={() => openClient(r.client)} className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-slate-50">
                  <span className="min-w-0"><span className="block truncate text-sm font-medium text-slate-800">{r.name}</span><span className="text-xs text-slate-500">{r.total === 0 ? "No tasks this period" : `${r.pending} pending · ${r.overdue} overdue`}</span></span>
                  <span className="flex shrink-0 gap-1">
                    {r.overdue > 0 && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700">{r.overdue} overdue</span>}
                    {r.pending > 0 && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">{r.pending} pending</span>}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Client Performance Ranking */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4">
          <div className="flex items-center gap-2"><Users className="h-4 w-4 text-indigo-500" /><h3 className="text-sm font-semibold text-slate-900">Client Performance Ranking</h3><span className="text-xs text-slate-500">· {dateRange.label}</span></div>
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative lg:max-w-xs lg:flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client by name or company…" data-testid="wp-client-search" className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-8 text-sm outline-none focus:border-indigo-400 focus:bg-white" />
              {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-slate-100"><X className="h-3.5 w-3.5 text-slate-400" /></button>}
            </div>
            <div className="flex items-center gap-2">
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="wp-sort" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700">
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <div className="flex rounded-lg border border-slate-200 p-0.5">
                <button onClick={() => setViewMode("list")} data-testid="wp-view-list" title="List view" className={`rounded-md p-1.5 ${viewMode === "list" ? "bg-slate-900 text-white" : "text-slate-500"}`}><ListIcon className="h-4 w-4" /></button>
                <button onClick={() => setViewMode("card")} data-testid="wp-view-card" title="Card view" className={`rounded-md p-1.5 ${viewMode === "card" ? "bg-slate-900 text-white" : "text-slate-500"}`}><LayoutGrid className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {filterPills.map(([key, label, active, onClick]) => (
              <button key={key} onClick={onClick} data-testid={`wp-filter-${key}`} className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${active ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{label}</button>
            ))}
            {/* Service-tag filter */}
            <div ref={tagMenuRef} className="relative">
              <button onClick={() => setTagMenuOpen((v) => !v)} data-testid="wp-tag-filter" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${tagFilter.length ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                <TagIcon className="h-3.5 w-3.5" /> {tagFilter.length ? `${tagFilter.length} tag${tagFilter.length > 1 ? "s" : ""}` : "Service Tag"}
              </button>
              {tagMenuOpen && (
                <div className="absolute left-0 z-30 mt-1 w-72 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
                  <div className="mb-1.5 flex items-center gap-1 rounded-lg bg-slate-100 p-0.5 text-xs">
                    {[["any", "Match any"], ["all", "Match all"]].map(([v, l]) => (
                      <button key={v} onClick={() => setTagMatch(v)} className={`flex-1 rounded-md px-2 py-1.5 font-medium ${tagMatch === v ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500"}`}>{l}</button>
                    ))}
                  </div>
                  <TagCheckList tags={tags} selected={tagFilter} maxHeight="16rem" autoFocus
                    onToggle={(id) => setTagFilter((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]))} />
                  {tagFilter.length > 0 && <button onClick={() => setTagFilter([])} className="mt-1 w-full rounded-lg px-2 py-1.5 text-center text-xs font-medium text-slate-500 hover:bg-slate-50">Clear tags</button>}
                </div>
              )}
            </div>
            {canManageTags && (
              <button onClick={() => setManageOpen(true)} data-testid="wp-manage-tags" title="Manage Tags"
                className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-200 transition hover:bg-indigo-100">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600"><TagIcon className="h-3 w-3 text-white" /></span>
                Manage Tags
              </button>
            )}
          </div>
        </div>

        {loadingClients ? (
          <div className="py-12 text-center text-sm text-slate-400">Loading clients…</div>
        ) : sortedRows.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">{clients.length === 0 ? <>No clients yet. Add clients under <Link to="/clients" className="font-medium text-indigo-600 hover:underline">Clients</Link>.</> : "No clients match these filters."}</div>
        ) : viewMode === "list" ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2.5 w-12">Rank</th><th className="px-3 py-2.5">Client</th><th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5 text-right">Total</th><th className="px-3 py-2.5 text-right">Done</th><th className="px-3 py-2.5 text-right">In Prog.</th>
                  <th className="px-3 py-2.5 text-right">Pending</th><th className="px-3 py-2.5 text-right">Overdue</th><th className="px-3 py-2.5">Completion</th><th className="px-3 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.slice(0, visible).map((r, i) => (
                  <tr key={r.id} onClick={() => openClient(r.client)} data-testid={`wp-row-${r.id}`} className={`cursor-pointer border-t border-slate-100 hover:bg-indigo-50/40 ${i % 2 ? "bg-slate-50/40" : ""}`}>
                    <td className="px-3 py-2.5 text-center text-sm font-semibold text-slate-400">{i + 1}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-50 to-slate-100 text-xs font-semibold text-indigo-700">{initials(r.name)}</span>
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-slate-900">{r.name}</span>
                          {r.loc && <span className="block truncate text-xs text-slate-500">{r.loc}</span>}
                          <span className="mt-0.5 block"><TagPills ids={r.client.client_tags} tagById={tagById} max={4} /></span>
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">{canDelete ? <StatusSelect status={r.status} onChange={(s) => changeStatus(r.client, s)} /> : <StatusBadge status={r.status} />}</td>
                    <td className="px-3 py-2.5 text-right font-medium text-slate-800">{r.total}</td>
                    <td className="px-3 py-2.5 text-right text-green-600">{r.completed}</td>
                    <td className="px-3 py-2.5 text-right text-blue-600">{r.in_progress}</td>
                    <td className="px-3 py-2.5 text-right text-amber-600">{r.pending}</td>
                    <td className="px-3 py-2.5 text-right font-medium text-rose-600">{r.overdue || ""}</td>
                    <td className="px-3 py-2.5"><ProgressBar pct={r.pct} /></td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="relative inline-flex items-center">
                        {canManageTags && (
                          <button onClick={(e) => { e.stopPropagation(); setTagEditorFor(tagEditorFor === r.id ? null : r.id); }} title="Edit tags" className="mr-1 rounded p-1 text-slate-300 hover:bg-indigo-50 hover:text-indigo-600"><TagIcon className="h-3.5 w-3.5" /></button>
                        )}
                        {canDelete && <button onClick={(e) => handleDelete(e, r.client)} disabled={deletingId === r.id} title="Delete client" className="mr-1 rounded p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-600">{deletingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}</button>}
                        <ArrowRight className="inline h-4 w-4 text-slate-300" />
                        {tagEditorFor === r.id && <TagEditorPopover tags={tags} selected={clientTagIds(r.client)} onToggle={(id) => { const cur = clientTagIds(r.client); saveClientTags(r.client, cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]); }} onClose={() => setTagEditorFor(null)} />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2.5 p-4 sm:grid-cols-2 lg:grid-cols-3">
            {sortedRows.slice(0, visible).map((r) => (
              <div key={r.id} role="button" tabIndex={0} onClick={() => openClient(r.client)} onKeyDown={(e) => { if (e.key === "Enter") openClient(r.client); }} data-testid={`wp-client-card-${r.id}`}
                className="group rounded-xl border border-slate-200 bg-white p-3 outline-none transition hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-50 to-slate-100 text-sm font-semibold text-indigo-700">{initials(r.name)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2"><span className="truncate text-sm font-medium text-slate-900">{r.name}</span>{canDelete ? <StatusSelect status={r.status} onChange={(s) => changeStatus(r.client, s)} /> : <StatusBadge status={r.status} />}</div>
                    {r.loc && <span className="block truncate text-xs text-slate-500">{r.loc}</span>}
                  </div>
                  <div className="relative flex shrink-0 items-center">
                    {canManageTags && <button onClick={(e) => { e.stopPropagation(); setTagEditorFor(tagEditorFor === r.id ? null : r.id); }} title="Edit tags" className="rounded p-1 text-slate-300 hover:bg-indigo-50 hover:text-indigo-600"><TagIcon className="h-3.5 w-3.5" /></button>}
                    <ArrowRight className="h-4 w-4 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-indigo-500" />
                    {tagEditorFor === r.id && <TagEditorPopover tags={tags} selected={clientTagIds(r.client)} onToggle={(id) => { const cur = clientTagIds(r.client); saveClientTags(r.client, cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]); }} onClose={() => setTagEditorFor(null)} />}
                  </div>
                </div>
                {clientTagIds(r.client).length > 0 && <div className="mt-2"><TagPills ids={r.client.client_tags} tagById={tagById} max={6} /></div>}
                <div className="mt-2.5"><ProgressBar pct={r.pct} /></div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">{r.total} tasks</span>
                  <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-700">{r.completed} done</span>
                  {r.pending > 0 && <span className="rounded bg-amber-50 px-1.5 py-0.5 font-medium text-amber-700">{r.pending} pending</span>}
                  {r.overdue > 0 && <span className="rounded bg-rose-50 px-1.5 py-0.5 font-medium text-rose-700">{r.overdue} overdue</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {sortedRows.length > visible && (
          <div className="border-t border-slate-100 p-3 text-center">
            <button onClick={() => setVisible((v) => v + 20)} className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">Load more ({visible} of {sortedRows.length})</button>
          </div>
        )}
      </div>

      {/* Add client */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader><DialogTitle>Add new client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-600">Client name *</label><input value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} autoFocus data-testid="wp-new-client-name" placeholder="e.g. Rohilkhand" className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" /></div>
              <div><label className="text-xs font-medium text-slate-600">Status</label><select value={form.client_status} onChange={(e) => setForm((f) => ({ ...f, client_status: e.target.value }))} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm">{Object.entries(CLIENT_STATUS).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}</select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-600">Company</label><input value={form.company_name} onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" /></div>
              <div><label className="text-xs font-medium text-slate-600">City</label><input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" /></div>
              <div><label className="text-xs font-medium text-slate-600">Contact person</label><input value={form.contact_person_name} onChange={(e) => setForm((f) => ({ ...f, contact_person_name: e.target.value }))} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" /></div>
              <div><label className="text-xs font-medium text-slate-600">Phone</label><input value={form.contact_phone} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" /></div>
              <div><label className="text-xs font-medium text-slate-600">Email</label><input value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" /></div>
              <div><label className="text-xs font-medium text-slate-600">Service</label><input value={form.service_taken} onChange={(e) => setForm((f) => ({ ...f, service_taken: e.target.value }))} placeholder="e.g. SEO, Social" className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" /></div>
            </div>
            <div><label className="text-xs font-medium text-slate-600">Notes</label><textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></div>
            {tags.length > 0 && (
              <div>
                <label className="text-xs font-medium text-slate-600">Service tags</label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {tags.map((tg) => {
                    const on = (form.client_tags || []).includes(tg.tag_id);
                    return (
                      <button key={tg.tag_id} type="button" onClick={() => setForm((f) => ({ ...f, client_tags: on ? f.client_tags.filter((x) => x !== tg.tag_id) : [...(f.client_tags || []), tg.tag_id] }))}
                        className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[13px] font-medium ring-1 ring-inset transition"
                        style={on ? { backgroundColor: (tg.color || "#6366f1") + "1a", color: tg.color, borderColor: tg.color } : { color: "#64748b" }}>
                        {on && <Check className="h-3 w-3" />}{tg.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setAddOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={handleCreate} disabled={adding} data-testid="wp-new-client-save" className="rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">{adding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add client"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ManageTagsDialog open={manageOpen} onClose={() => setManageOpen(false)} tags={tags} businessId={businessId} onChanged={() => { loadTags(); fetchClients(); }} />
    </div>
  );
}

// Admin catalog editor: create / rename / recolor / delete service tags.
function ManageTagsDialog({ open, onClose, tags, businessId, onChanged }) {
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#6366f1");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("#6366f1");

  const create = async () => {
    if (!newName.trim()) return toast.error("Tag name is required");
    setBusy(true);
    try { await workPlannerApi.createTag({ name: newName.trim(), color: newColor, business_id: businessId || undefined }); setNewName(""); setAdding(false); onChanged(); toast.success("Tag added"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const saveEdit = async (tg) => {
    const name = editName.trim();
    if (!name) return toast.error("Tag name is required");
    if (name === tg.name && editColor === (tg.color || "#6366f1")) return setEditingId(null);
    try { await workPlannerApi.updateTag(tg.tag_id, { name, color: editColor }); setEditingId(null); onChanged(); toast.success("Tag updated"); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (tg) => { if (!window.confirm(`Delete tag "${tg.name}"? It will be removed from all clients.`)) return; try { await workPlannerApi.deleteTag(tg.tag_id); onChanged(); toast.success("Tag deleted"); } catch (e) { toast.error(e.message); } };
  const startEdit = (tg) => { setEditingId(tg.tag_id); setEditName(tg.name); setEditColor(tg.color || "#6366f1"); };

  const shown = tags.filter((tg) => matchTag(tg, q));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600"><TagIcon className="h-4 w-4 text-white" /></span>
            Tag Manager
          </DialogTitle>
          <p className="pl-10 text-sm text-slate-500">Create and manage client service tags.</p>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="wp-tagmgr-search" placeholder="Search tags…"
              className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-8 text-sm text-slate-800 placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none" />
            {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>}
          </div>
          <Button onClick={() => setAdding((v) => !v)} data-testid="wp-tagmgr-add" className="h-10 shrink-0 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"><Plus className="mr-1 h-4 w-4" /> Add Tag</Button>
        </div>

        {adding && (
          <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50/50 p-2">
            <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} title="Tag color" className="h-9 w-10 shrink-0 cursor-pointer rounded border border-slate-200 bg-white" />
            <input value={newName} autoFocus onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") create(); if (e.key === "Escape") setAdding(false); }}
              placeholder="New tag name, e.g. Podcast" className="h-9 flex-1 rounded-lg border border-slate-300 px-3 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none" />
            <Button onClick={create} disabled={busy} className="h-9 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
            <button onClick={() => setAdding(false)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
          </div>
        )}

        <div className="max-h-80 space-y-1 overflow-y-auto">
          {tags.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No tags yet — add your first service tag above.</div>
          ) : shown.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No tags found</div>
          ) : shown.map((tg) => (
            <div key={tg.tag_id} className="flex items-center gap-2 rounded-lg px-1 py-1.5 hover:bg-slate-50">
              {editingId === tg.tag_id ? (
                <>
                  <input type="color" value={editColor} onChange={(e) => setEditColor(e.target.value)} title="Color" className="h-9 w-10 shrink-0 cursor-pointer rounded border border-slate-200" />
                  <input value={editName} autoFocus onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveEdit(tg); if (e.key === "Escape") setEditingId(null); }}
                    className="h-9 flex-1 rounded-lg border border-indigo-300 px-3 text-sm text-slate-800 focus:outline-none" />
                  <Button onClick={() => saveEdit(tg)} className="h-9 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">Save</Button>
                  <button onClick={() => setEditingId(null)} title="Cancel" className="rounded p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
                </>
              ) : (
                <>
                  <span className="inline-flex items-center rounded-md px-2.5 py-1 text-[14px] font-medium" style={{ backgroundColor: (tg.color || "#6366f1") + "1a", color: tg.color || "#6366f1" }}>{tg.name}</span>
                  <span className="flex-1" />
                  <button onClick={() => startEdit(tg)} title="Rename tag" className="rounded-lg p-2 text-slate-400 transition hover:bg-indigo-50 hover:text-indigo-600"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(tg)} title="Delete tag" className="rounded-lg p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
          <span className="text-xs text-slate-400">{tags.length} tag{tags.length === 1 ? "" : "s"}{q && shown.length !== tags.length ? ` · ${shown.length} shown` : ""} · deleting a tag removes it from all clients</span>
          <Button variant="outline" onClick={onClose} className="rounded-lg">Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
